import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase-config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { notification, Button, Input, Modal, Form, Space, Card, Typography, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

const FaqEditor = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [form] = Form.useForm();

  // Fetch FAQ data
  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const faqCollection = collection(db, 'faq');
      const q = query(faqCollection, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const faqData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFaqs(faqData);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load FAQ data.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingFaq(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    form.setFieldsValue({
      question: faq.question,
      answer: faq.answer,
      order: faq.order
    });
    setModalVisible(true);
  };

  const handleDelete = async (faqId) => {
    try {
      await deleteDoc(doc(db, 'faq', faqId));
      notification.success({
        message: 'Success',
        description: 'FAQ deleted successfully.',
        placement: 'topRight',
      });
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to delete FAQ.',
        placement: 'topRight',
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingFaq) {
        // Update existing FAQ
        await updateDoc(doc(db, 'faq', editingFaq.id), {
          question: values.question,
          answer: values.answer,
          order: parseInt(values.order),
          updatedAt: new Date().toISOString()
        });
        notification.success({
          message: 'Success',
          description: 'FAQ updated successfully.',
          placement: 'topRight',
        });
      } else {
        // Add new FAQ
        const newOrder = faqs.length > 0 ? Math.max(...faqs.map(f => f.order)) + 1 : 1;
        await addDoc(collection(db, 'faq'), {
          question: values.question,
          answer: values.answer,
          order: newOrder,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        notification.success({
          message: 'Success',
          description: 'FAQ added successfully.',
          placement: 'topRight',
        });
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to save FAQ.',
        placement: 'topRight',
      });
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    try {
      const newFaqs = [...faqs];
      const [movedItem] = newFaqs.splice(fromIndex, 1);
      newFaqs.splice(toIndex, 0, movedItem);

      // Update order for all items
      const updatePromises = newFaqs.map((faq, index) => 
        updateDoc(doc(db, 'faq', faq.id), { order: index + 1 })
      );

      await Promise.all(updatePromises);
      notification.success({
        message: 'Success',
        description: 'FAQ order updated successfully.',
        placement: 'topRight',
      });
      fetchFaqs();
    } catch (error) {
      console.error('Error reordering FAQs:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to reorder FAQs.',
        placement: 'topRight',
      });
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Manage FAQ</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
          size="large"
        >
          Add New FAQ
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text>Loading FAQs...</Text>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {faqs.map((faq, index) => (
            <Card 
              key={faq.id} 
              size="small"
              style={{ border: '1px solid #d9d9d9' }}
              actions={[
                <Button 
                  icon={<UpOutlined />} 
                  onClick={() => {
                    if (index > 0) handleReorder(index, index - 1);
                  }}
                  disabled={index === 0}
                >
                  Move Up
                </Button>,
                <Button 
                  icon={<DownOutlined />} 
                  onClick={() => {
                    if (index < faqs.length - 1) handleReorder(index, index + 1);
                  }}
                  disabled={index === faqs.length - 1}
                >
                  Move Down
                </Button>,
                <Button 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(faq)}
                >
                  Edit
                </Button>,
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDelete(faq.id)}
                >
                  Delete
                </Button>
              ]}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                    {faq.question}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Order: {faq.order}
                  </Text>
                  <Divider style={{ margin: '8px 0' }} />
                  <div 
                    style={{ 
                      maxHeight: 100, 
                      overflow: 'hidden',
                      fontSize: '14px',
                      color: '#666'
                    }}
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            question: '',
            answer: '',
            order: 1
          }}
        >
          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true, message: 'Please enter the question!' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="Enter the FAQ question..."
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="answer"
            label="Answer"
            rules={[{ required: true, message: 'Please enter the answer!' }]}
          >
            <ReactQuill
              theme="snow"
              placeholder="Enter the FAQ answer..."
              style={{ height: 200 }}
            />
          </Form.Item>

          <Form.Item
            name="order"
            label="Display Order"
            rules={[{ required: true, message: 'Please enter the display order!' }]}
          >
            <Input 
              type="number" 
              min={1}
              placeholder="Enter display order (1, 2, 3...)"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingFaq ? 'Update FAQ' : 'Add FAQ'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FaqEditor; 